
import type {
  CustomerDetailApi,
  CustomerFinancialApi,
  CustomerAgingApi,
  InvoiceApi,
  PaymentApi,
  CommunicationApi,
  ContactApi,
  CustomerAddressApi,
} from "@/types/api/customer-detail";
import type {
  CustomerDetailUI,
  CustomerFinancialUI,
  CustomerAgingUI,
  InvoiceUI,
  PaymentUI,
  CommunicationUI,
  ContactUI,
  CustomerAddressUI,
} from "@/types/ui/customer-detail";

function mapCurrency(currency: string): "USD" | "EUR" | "GBP" | "CAD" | "AUD" {
  const normalized = currency.toUpperCase();
  if (["USD", "EUR", "GBP", "CAD", "AUD"].includes(normalized)) {
    return normalized as "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  }
  return "USD"; // Default fallback
}

function mapPaymentTerms(paymentTerms: string): "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90" {
  if (["Net 15", "Net 30", "Net 45", "Net 60", "Net 90"].includes(paymentTerms)) {
    return paymentTerms as "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  }
  return "Net 30"; // Default fallback
}

export function mapCustomerDetailApiToUi(api: CustomerDetailApi): CustomerDetailUI {
  return {
    id: api.id,
    name: api.name,
    accountNumber: api.accountNumber,
    currency: mapCurrency(api.currency),
    outstanding: api.outstanding,
    overdue: api.overdue,
    creditLimit: api.creditLimit,
    dso: api.dso ?? 0,
    risk: api.risk,
    lastPayment: api.lastPayment || "",
    contact: api.contact,
    phone: api.phone,
    address: api.address,
    paymentTerms: mapPaymentTerms(api.paymentTerms),
    collectionStrategyId: api.collectionStrategyId || "",
    creditControllerId: api.creditControllerId || "",
    accountManagerId: api.accountManagerId,
    riskLevelId: api.riskLevelId,
    riskLevelName: api.riskLevelName,
    onStop: api.onStop ?? false,
    status: api.status || "Active",
    userSortKey1: api.userSortKey1 || "",
    userSortKey2: api.userSortKey2 || "",
    userSortKey3: api.userSortKey3 || "",
    userSortKey4: api.userSortKey4 || "",
    userSortKey1Label: api.userSortKey1Label || "User Sort Key 1",
    userSortKey2Label: api.userSortKey2Label || "User Sort Key 2",
    userSortKey3Label: api.userSortKey3Label || "User Sort Key 3",
    userSortKey4Label: api.userSortKey4Label || "User Sort Key 4",
    dueDays: api.dueDays,
    dueDaysType: api.dueDaysType,
  };
}

export function mapCustomerFinancialApiToUi(api: CustomerFinancialApi): CustomerFinancialUI {
  return {
    outstanding: api.outstanding,
    overdue: api.overdue,
    creditLimit: api.creditLimit,
    availableCredit: api.availableCredit,
    paymentTerms: api.paymentTerms,
    dso: api.dso,
    risk: api.risk,
    creditUtilizationPercent: api.creditUtilizationPercent,
  };
}

function sumBandOutstanding(bands: CustomerAgingApi["bands"], predicate: (b: CustomerAgingApi["bands"][number]) => boolean): number {
  return bands.filter(predicate).reduce((acc, x) => acc + x.totalOutstanding, 0);
}

export function mapCustomerAgingApiToUi(api: CustomerAgingApi): CustomerAgingUI {
  const current = sumBandOutstanding(api.bands, (x) => x.startDays === 0);
  const days1_30 = sumBandOutstanding(api.bands, (x) => x.startDays > 0 && x.startDays <= 30);
  const days31_60 = sumBandOutstanding(api.bands, (x) => x.startDays > 30 && x.startDays <= 60);
  const days61_90 = sumBandOutstanding(api.bands, (x) => x.startDays > 60 && x.startDays <= 90);
  const days90Plus = sumBandOutstanding(api.bands, (x) => x.startDays > 90);
  const total = api.totalOutstanding;
  const overdue = api.totalOverdue;

  return { current, days1_30, days31_60, days61_90, days90Plus, total, overdue };
}

export function mapInvoiceApiToUi(api: InvoiceApi): InvoiceUI {
  return {
    id: api.invoiceNumber,
    date: api.date,
    dueDate: api.dueDate,
    amount: api.amount,
    status: api.status,
  };
}

export function mapPaymentApiToUi(api: PaymentApi): PaymentUI {
  return {
    id: api.paymentNumber,
    date: api.date,
    amount: api.amount,
    method: api.method,
    reference: api.reference,
    allocations: api.allocations.map((allocation) => ({
      invoiceId: allocation.invoiceNumber,
      invoiceDate: allocation.invoiceDate,
      amount: allocation.amount,
    })),
  };
}

export function mapCommunicationApiToUi(api: CommunicationApi): CommunicationUI {
  return {
    id: api.id,
    customerId: api.customerId,
    customerName: api.customerName,
    contactName: api.contactName,
    type: api.type,
    subject: api.subject,
    content: api.content,
    status: api.status,
    direction: api.direction,
    source: api.source,
    createdAt: api.createdAt,
    createdBy: api.createdBy,
    metadata: api.metadata,
  };
}

export function mapContactApiToUi(api: ContactApi): ContactUI {
  return {
    id: api.id,
    title: api.title,
    firstName: api.firstName,
    surname: api.surname,
    email: api.email,
    phone: api.phone,
    mobile: api.mobile,
    preferredContactMethod: api.preferredContactMethod,
    jobTitle: api.jobTitle || '',
    company: api.company,
    customerId: api.customerId,
    address: api.address || '',
    notes: api.notes,
    createdAt: api.createdAt,
    lastContact: api.lastContact,
    isPrimary: api.isPrimary,
  };
}

export function mapCustomerAddressApiToUi(api: CustomerAddressApi): CustomerAddressUI {
  return {
    id: api.id,
    customerId: api.customerId,
    externalId: api.externalId,
    accountCode: api.accountCode,
    contactName: api.contactName,
    email: api.email,
    phone: api.phone,
    fax: api.fax,
    addressLine: api.addressLine,
    town: api.town,
    county: api.county,
    postCode: api.postCode,
    country: api.country,
    countryCode: api.countryCode,
    isInvoice: api.isInvoice,
    isStatement: api.isStatement,
    isDelivery: api.isDelivery,
    isDeliveryDefault: api.isDeliveryDefault ?? false,
    isPrimary: api.isPrimary,
    contactId: api.contactId,
    notes: api.notes,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
}
